import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  Checkbox,
  Row,
  Col,
  DatePicker,
  Select,
  Space,
  App,
  Spin,
  Collapse,
  Divider,
  Typography,
  Tooltip
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  FilePdfOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckOutlined,
  SendOutlined,
  CloseOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import dayjs from 'dayjs';
import WarehouseQuantityField from '../modules/warehouse/WarehouseQuantityField';
import {
  WAREHOUSE_CONVERTED_QUANTITY_MAX,
  formatWarehouseQuantity,
  getWarehouseLineUnit,
  type WarehouseQuantityPatch,
} from '../modules/warehouse/warehouseQuantityConversion';
import { formatProductDimensions } from '../utils/productDimensions';
import { formatUnit } from '../utils/units';
import { formatConversionValue } from '../utils/dimensionConversion';
import {
  WZItem,
  WZStatus,
  CreateWZDto,
  UpdateWZDto
} from '../types/wz';
import {
  WZDocumentDto,
  attachWZItemDimensions,
  buildRecipientFieldsFromCustomer,
  normalizeWZDocument,
  prepareWZItemsForSave
} from '../dto/wz';
import { OrderOptionDto, normalizeOrderOptions } from '../dto/order';
import { canManageWZDocuments } from '../modules/warehouse/warehouseDocumentAccessPolicy';
import { getEditableWZStatusOptions, isWZEditable } from '../utils/wzEditRules';
import ProductNameCombobox from '../components/inventory/ProductNameCombobox';
import CustomerNameCombobox from '../components/inventory/CustomerNameCombobox';
import { buildWzVariantAutofill } from '../utils/wzVariantSelection';
import type { ProductVariant } from '../types/pz';
import { fetchOrderTransports } from '../modules/orders/api/orderTransportApi';
import type { TransportRouteSummaryDto } from '../dto/transport';
import { mergeWZTransportOptions } from '../utils/wzTransportSelection';
import WZTransportSummary from '../modules/wz/components/WZTransportSummary';
import { WZItemCard, WZItemField, WZItemsList } from './WZItemsView';
import {
  WZ_QUANTITY_INVALID_CODE,
  WZ_QUANTITY_MAX,
} from '../utils/warehouseQuantity';
import './WZItemsView.css';

const { Option } = Select;
const { TextArea } = Input;
const createEmptyItem = (): Partial<WZItem> => ({ productName: '', quantity: 1, unit: 'pcs' });

const formatLineDimensions = (item: Partial<WZItem>) =>
  formatProductDimensions({
    width: item.width,
    height: item.height,
    length: item.length,
    thickness: item.thickness,
    productCategoryDimensionDisplayProfile: item.productCategoryDimensionDisplayProfile ?? 'GENERIC',
  }) || '-';

const WZDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(['wz', 'common', 'transport']);
  const { message, modal } = App.useApp();
  const [form] = Form.useForm();

  const isNewDocument = id === 'new';
  const queryParams = new URLSearchParams(location.search);
  const requestedEditMode = queryParams.get('mode') === 'edit' || isNewDocument;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wzDocument, setWzDocument] = useState<WZDocumentDto | null>(null);
  const [items, setItems] = useState<Partial<WZItem>[]>([createEmptyItem()]);
  const [orders, setOrders] = useState<OrderOptionDto[]>([]);
  const [transports, setTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [linkedTransports, setLinkedTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [deliveryDifferent, setDeliveryDifferent] = useState(false);

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageWZDocuments(currentUser);
  const canDownloadWZPdf = authService.canDownloadWZDocumentPdf();
  const currentUserDisplayName = currentUser?.name || '';
  const canEditDocument = canEdit && (isNewDocument || isWZEditable(wzDocument?.status));
  const isEditMode = requestedEditMode && canEditDocument;
  const selectedOrderId = Form.useWatch('orderId', form);

  useEffect(() => {
    if (!isNewDocument) {
      fetchWZDocument();
    }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const orderIdForTransports = String(selectedOrderId || '').trim();

    if (!orderIdForTransports) {
      setTransports(linkedTransports);
      return () => { cancelled = true; };
    }

    const fetchTransports = async () => {
      try {
        const result = await fetchOrderTransports(orderIdForTransports);
        if (!cancelled) {
          setTransports(mergeWZTransportOptions(result.transports, linkedTransports));
        }
      } catch (error: any) {
        console.error('Error fetching order transports:', error);
        if (!cancelled) {
          setTransports(linkedTransports);
        }
      }
    };

    fetchTransports();
    return () => { cancelled = true; };
  }, [selectedOrderId, linkedTransports]);

  const fetchWZDocument = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/wz/${id}`);
      const doc = normalizeWZDocument(response.data, 'wz.detail.document');
      setWzDocument(doc);

      // Set form values
      form.setFieldsValue({
        ...doc,
        issueDate: doc.issueDate ? dayjs(doc.issueDate) : undefined,
        releaseDate: doc.releaseDate ? dayjs(doc.releaseDate) : undefined,
        receivedDate: doc.receivedDate ? dayjs(doc.receivedDate) : undefined,
        orderId: doc.orderId,
        transportIds: doc.transportIds || [],
      });
      const docLinkedTransports = Array.isArray(doc.linkedTransports) ? doc.linkedTransports : [];
      setLinkedTransports(docLinkedTransports);
      setTransports((previous) => mergeWZTransportOptions(previous, docLinkedTransports));

      // The delivery address input only shows when it actually diverges from
      // the recipient address — a WZ saved with delivery == recipient opens
      // with the toggle off (backend already treats empty deliveryAddress as
      // "use recipientAddress", see handleSave below).
      const loadedDeliveryAddress = (doc.deliveryAddress || '').trim();
      const loadedRecipientAddress = (doc.recipientAddress || '').trim();
      setDeliveryDifferent(loadedDeliveryAddress !== '' && loadedDeliveryAddress !== loadedRecipientAddress);

      // Attach the linked order's product dimensions so the per-line m²/m³
      // conversion can be shown/recomputed in the form (mirrors PDF resolution).
      setItems(doc.itemsLoaded
        ? attachWZItemDimensions(doc.items, doc.orderProducts)
        : [createEmptyItem()]);
    } catch (error: any) {
      message.error(error.response?.data?.error || t('wz:error.fetchFailed'));
      navigate('/app/wz');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(normalizeOrderOptions(response.data, 'wz.detail.orders'));
    } catch (error: any) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleOrderChange = async (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (!wzDocument || orderId !== wzDocument.orderId) {
      setLinkedTransports([]);
    }
    form.setFieldsValue({
      transportIds: [],
      transportCompany: undefined,
      transportPrice: undefined,
      transportInvoiceNo: undefined,
      vehicleRegistration: undefined,
      driverName: undefined,
    });

    if (order) {
      form.setFieldsValue({
        recipientName: order.customer?.name || '',
        recipientAddress: order.customer?.address || '',
        recipientPhone: order.customer?.phone || '',
        recipientEmail: order.customer?.email || '',
      });

      // Deliberately no item seeding here: handleOrderChange only fires
      // when `isNewDocument` (id === 'new'), but App.tsx routes `wz/new` to
      // WZNew.tsx exclusively — `wz/:id` (this page) never receives the
      // literal id "new" — so this branch of the form is unreachable through
      // normal navigation. Order-driven WZ item seeding has exactly one live
      // home: the WZ create domain seam, which reads
      // GET /wz/prefill/:orderId for the remaining-quantity-netted,
      // variant-linked item list. See frontend/AGENTS.md.
    }
  };

  const handleSave = async (values: any) => {
    if (!canEditDocument) {
      message.error(t('wz:error.saveFailed'));
      return;
    }

    if (!isNewDocument && wzDocument && !wzDocument.itemsLoaded) {
      message.error(t('wz:error.itemsUnavailable'));
      return;
    }

    setSaving(true);
    try {
      const safeItems = prepareWZItemsForSave(items).map((item) => {
        const { unitPrice, totalValue, notes, ...safeItem } = item;
        return safeItem;
      });
      const data: CreateWZDto | UpdateWZDto = {
        ...values,
        wzNumber: values.wzNumber?.trim() || undefined,
        issueDate: values.issueDate?.format('YYYY-MM-DD'),
        releaseDate: wzDocument?.releaseDate || values.issueDate?.format('YYYY-MM-DD'),
        receivedDate: values.receivedDate ? values.receivedDate.format('YYYY-MM-DD') : null,
        transportIds: transports
          .filter((transport) => transport.status !== 'CANCELLED')
          .map((transport) => transport.id),
        items: safeItems,
        // The delivery input is hidden (and its stored value stale) whenever
        // the "different than registered" toggle is off — force it empty so
        // the backend falls back to recipientAddress instead of persisting a
        // leftover value from before the toggle was switched off.
        deliveryAddress: deliveryDifferent ? (values.deliveryAddress || '') : '',
      };
      delete data.status;
      delete (data as { costOfGoodsSold?: number }).costOfGoodsSold;
      delete data.producerInvoice;
      delete data.purchaseInvoiceAmount;
      delete data.transportPrice;
      delete data.transportInvoiceNo;
      delete data.transportCompany;
      delete data.driverName;
      delete data.transportNotes;

      if (isNewDocument) {
        const response = await api.post('/wz', data);
        message.success(t('wz:success.created'));
        const createdId = response?.data?.id;
        navigate(createdId ? `/app/wz/${createdId}` : '/app/wz');
      } else {
        await api.put(`/wz/${id}`, data);
        message.success(t('wz:success.updated'));
        // Stay on the document: drop ?mode=edit (back to view mode) and refresh.
        navigate(`/app/wz/${id}`, { replace: true });
        fetchWZDocument();
      }
    } catch (error: any) {
      // Backend has no i18n — it emits a stable `code` for the
      // RELEASED/RECEIVED update-block (see
      // backend/src/services/warehouseDocumentStatusPolicy.js) and we
      // translate by code here, matching the WZ_NUMBER_EXISTS convention.
      const code = error.response?.data?.code;
      message.error(
        code === 'WZ_NUMBER_EXISTS'
          ? t('wz:error.wzNumberExists')
          : code === WZ_QUANTITY_INVALID_CODE
            ? t('wz:serverErrors.quantityPrecision')
          : code === 'WZ_UPDATE_BLOCKED_RELEASED'
            ? t('wz:error.updateBlockedReleased')
          : code === 'WZ_UPDATE_BLOCKED_RECEIVED'
            ? t('wz:error.updateBlockedReceived')
          : (error.response?.data?.error || t('wz:error.saveFailed'))
      );
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (
    newStatus: WZStatus,
    options?: { confirmExceedsOrderedQuantity?: boolean; confirmationFingerprint?: string },
  ) => {
    try {
      await api.put(`/wz/${id}/status`, {
        status: newStatus,
        ...(options?.confirmExceedsOrderedQuantity ? { confirmExceedsOrderedQuantity: true } : {}),
        ...(options?.confirmationFingerprint ? { confirmationFingerprint: options.confirmationFingerprint } : {}),
      });
      message.success(t('wz:success.statusUpdated'));
      fetchWZDocument();
    } catch (error: any) {
      const data = error.response?.data;
      if (data?.code === 'WZ_RELEASE_EXCEEDS_ORDERED_QUANTITY') {
        // A WARNING, not a hard block — over-delivery is allowed in real
        // logistics (see backend/src/services/wzService.js
        // findLinesExceedingOrderedQuantity). Backend has no i18n: it sends
        // an English message + `code` + structured `lines`, which we render
        // as a Polish confirmation naming each affected line.
        const lines: Array<{
          productName: string;
          unit: string;
          orderedQuantity: number;
          alreadyReleasedQuantity: number;
          addingQuantity: number;
          projectedQuantity: number;
        }> = Array.isArray(data.lines) ? data.lines : [];
        const unitMismatchLines: Array<{
          productName: string;
          unit: string;
          orderUnits: string[];
          quantity: number;
          source: 'releasedSibling' | 'thisDocument';
        }> = Array.isArray(data.unitMismatchLines) ? data.unitMismatchLines : [];
        const confirmed = await new Promise<boolean>((resolve) => {
          modal.confirm({
            title: t('wz:confirm.exceedsOrderedQuantityTitle'),
            width: 560,
            content: (
              <div>
                {lines.length > 0 && <p>{t('wz:confirm.exceedsOrderedQuantityIntro')}</p>}
                <ul>
                  {lines.map((line, index) => (
                    <li key={`${line.productName}-${index}`}>
                      {t('wz:confirm.exceedsOrderedQuantityLine', {
                        productName: line.productName,
                        ordered: formatWarehouseQuantity(line.orderedQuantity, line.unit, i18n.language),
                        alreadyReleased: formatWarehouseQuantity(line.alreadyReleasedQuantity, line.unit, i18n.language),
                        adding: formatWarehouseQuantity(line.addingQuantity, line.unit, i18n.language),
                        projected: formatWarehouseQuantity(line.projectedQuantity, line.unit, i18n.language),
                      })}
                    </li>
                  ))}
                  {unitMismatchLines.map((line, index) => (
                    <li key={`unit-${line.productName}-${index}`}>
                      {t('wz:confirm.unitMismatchLine', {
                        productName: line.productName,
                        quantity: formatWarehouseQuantity(line.quantity, line.unit, i18n.language),
                        orderUnits: line.orderUnits.map((unit) => formatUnit(unit, i18n.language)).join(', '),
                        source: t(`wz:confirm.unitMismatchSource.${line.source}`),
                      })}
                    </li>
                  ))}
                </ul>
                <p>{t('wz:confirm.exceedsOrderedQuantityFooter')}</p>
              </div>
            ),
            okText: t('wz:confirm.exceedsOrderedQuantityConfirm'),
            cancelText: t('common:cancel'),
            okType: 'danger',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (confirmed) {
          await handleStatusChange(newStatus, {
            confirmExceedsOrderedQuantity: true,
            confirmationFingerprint: data.confirmationFingerprint,
          });
        }
      } else if (data?.code === 'INSUFFICIENT_INVENTORY') {
        message.error(t('wz:error.insufficientInventory', {
          needed: data.needed,
          available: data.available,
        }));
      } else if (data?.code === 'WZ_INVALID_STATUS_TRANSITION') {
        // Backend has no i18n — translate by code, matching the
        // INSUFFICIENT_INVENTORY / WZ_NUMBER_EXISTS convention.
        message.error(t('wz:error.invalidStatusTransition'));
      } else if (data?.code === 'WZ_RELEASE_ITEMS_NOT_LINKED_TO_INVENTORY') {
        message.error(t('wz:error.itemsNotLinkedToInventory'));
      } else {
        message.error(data?.error || t('wz:error.statusUpdateFailed'));
      }
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.get(`/wz/${id}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${wzDocument?.wzNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(t('wz:success.pdfDownloaded'));
    } catch (error: any) {
      message.error(error.response?.data?.error || t('wz:error.pdfGenerateFailed'));
    }
  };

  const addItem = () => {
    setItems([...items, createEmptyItem()]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  /** Quantity and conversionUnit are decided together in the conversion modal,
   *  so they must land in one state update. `unit` stays untouched — it belongs
   *  to the catalog product, and the release consumes stock denominated in it. */
  const updateItemQuantity = (index: number, patch: WarehouseQuantityPatch) => {
    setItems((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      return {
          ...item,
          quantity: patch.quantity,
          conversionUnit: patch.conversionUnit,
          ...(patch.unit ? { unit: patch.unit } : {}),
          saleQuantity: patch.pricingQuantity ?? null,
          saleUnit: patch.pricingUnit ?? null,
      };
    }));
  };

  /** Autofill a WZ row from a catalog variant selection. */
  const handleVariantSelect = (index: number, variantId: string | undefined, variant?: ProductVariant) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      ...buildWzVariantAutofill(updatedItems[index], variantId, variant),
    };
    setItems(updatedItems);
  };

  /** Free-text edit of the product name combobox detaches any catalog link —
   *  the operator is describing a product not in the catalog (or has edited
   *  away from a previously picked one). A catalog pick right after this
   *  (handleVariantSelect, via onSelectVariant) fires as a separate event and
   *  supersedes it with the full autofill, so the two can't race. */
  const handleProductNameTyped = (index: number, productName: string) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], productName, productVariantId: undefined };
    setItems(updatedItems);
  };

  const statusTransitionOptions = wzDocument
    ? getEditableWZStatusOptions(wzDocument.status).filter(status => status !== wzDocument.status)
    : [];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/wz')}>
            {t('common:back')}
          </Button>
          <h1 style={{ margin: 0 }}>
            {isNewDocument ? t('wz:actions.create') : wzDocument?.wzNumber}
          </h1>
        </Space>
        <Space>
          {!isNewDocument && !isEditMode && canEditDocument && (
            <Button onClick={() => navigate(`/app/wz/${id}?mode=edit`)}>
              {t('common:edit')}
            </Button>
          )}
          {!isNewDocument && canDownloadWZPdf && (
            <Button icon={<FilePdfOutlined />} onClick={handleDownloadPDF}>
              {t('wz:actions.downloadPDF')}
            </Button>
          )}
        </Space>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        onFinishFailed={() => message.warning(t('wz:fillRequired'))}
        scrollToFirstError
        initialValues={{
          issueDate: dayjs(),
          issuedBy: currentUserDisplayName,
          receivedBy: currentUserDisplayName,
        }}
      >
        <Row gutter={24}>
          <Col span={24}>
            <Card title={t('wz:sections.basicInfo')} style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                {!isNewDocument && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      label={t('wz:fields.wzNumber')}
                      name="wzNumber"
                      rules={[
                        { required: true, message: t('wz:validation.wzNumberRequired') },
                        { max: 50, message: t('wz:validation.wzNumberMax') },
                      ]}
                    >
                      <Input disabled={!isEditMode} maxLength={50} />
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label={t('wz:fields.issueDate')}
                    name="issueDate"
                    rules={[{ required: true, message: t('wz:validation.issueDateRequired') }]}
                  >
                    <DatePicker style={{ width: '100%' }} disabled={!isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label={t('wz:fields.deliveryDate')}
                    name="receivedDate"
                    rules={[{ required: true, message: t('wz:validation.deliveryDateRequired') }]}
                  >
                    <DatePicker style={{ width: '100%' }} disabled={!isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    label={t('wz:fields.order')}
                    name="orderId"
                  >
                    <Select
                      placeholder={t('wz:placeholders.selectOrder')}
                      disabled={!isEditMode}
                      onChange={handleOrderChange}
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.children as any)?.toString().toLowerCase().includes(input.toLowerCase())
                      }
                    >
                        {orders.map(order => (
                          <Option key={order.id} value={order.id}>
                          {order.label || `${order.displayOrderNumber} - ${order.customerName || '-'}`}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col span={24}>
            <Card title={t('wz:sections.recipientAndDelivery')} style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  {/* Pick-or-type combobox: value IS recipientName, so a
                      loaded or freely-typed recipient always displays
                      natively. Picking a customer suggestion additionally
                      fills the rest of the recipient block via
                      buildRecipientFieldsFromCustomer; the fields stay
                      editable for a one-off recipient not in the list, and
                      picking an order afterwards can still override it. */}
                  <Form.Item
                    label={t('wz:fields.recipientName')}
                    name="recipientName"
                    rules={[{ required: true, message: t('wz:validation.recipientNameRequired') }]}
                  >
                    <CustomerNameCombobox
                      onSelectCustomer={(customer) => form.setFieldsValue(buildRecipientFieldsFromCustomer(customer))}
                      disabled={!isEditMode}
                      placeholder={t('wz:placeholders.selectCustomer')}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.recipientNIP')}
                    name="recipientNIP"
                  >
                    <Input disabled={!isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.recipientAddress')}
                    name="recipientAddress"
                    rules={[{ required: true, message: t('wz:validation.recipientAddressRequired') }]}
                  >
                    <Input disabled={!isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.recipientPhone')}
                    name="recipientPhone"
                  >
                    <Input disabled={!isEditMode} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.recipientEmail')}
                    name="recipientEmail"
                  >
                    <Input type="email" disabled={!isEditMode} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" plain>{t('wz:fields.deliveryAddress')}</Divider>

              <Row gutter={16}>
                <Col span={24}>
                  <Checkbox
                    checked={deliveryDifferent}
                    disabled={!isEditMode}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setDeliveryDifferent(on);
                      if (!on) {
                        form.setFieldsValue({ deliveryAddress: '' });
                      }
                    }}
                  >
                    {t('wz:delivery.differentFromRegistered')}
                  </Checkbox>
                </Col>
              </Row>

              {deliveryDifferent && (
                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col xs={24}>
                    <Form.Item
                      name="deliveryAddress"
                    >
                      <TextArea
                        rows={3}
                        disabled={!isEditMode}
                        placeholder={t('wz:placeholders.deliveryAddress')}
                        aria-label={t('wz:fields.deliveryAddress')}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Card>
          </Col>

          <Col span={24}>
            <Card
              title={t('wz:sections.items')}
              style={{ marginBottom: 24 }}
              className="wz-items-section"
              extra={
                isEditMode && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={addItem}>
                    {t('wz:actions.addItem')}
                  </Button>
                )
              }
            >
              <WZItemsList>
                {items.map((item, index) => {
                  const conversionLabel = formatConversionValue(
                    item.conversionUnit,
                    // normalizeQuantityUnit does not know the ProductUnit enum, so
                    // the source unit must already be a canonical line code.
                    { ...item, unit: getWarehouseLineUnit(item) },
                    Number(item.quantity) || 0,
                  );

                  return (
                    <WZItemCard
                      key={`${item.id || item.productVariantId || 'item'}-${index}`}
                      index={index}
                      positionLabel={t('wz:itemFields.position', { number: index + 1 })}
                      productLabel={t('wz:fields.productName')}
                      product={isEditMode ? (
                        <ProductNameCombobox
                          value={item.productName || ''}
                          onChange={(productName) => handleProductNameTyped(index, productName)}
                          onSelectVariant={(variantId, variant) => handleVariantSelect(index, variantId, variant)}
                          placeholder={t('wz:placeholders.productName')}
                        />
                      ) : item.productName}
                      action={isEditMode ? (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removeItem(index)}
                          disabled={items.length === 1}
                          aria-label={t('common:delete')}
                        />
                      ) : undefined}
                    >
                      <WZItemField label={t('wz:fields.dimensions')} wide>
                        {formatLineDimensions(item)}
                      </WZItemField>
                      <WZItemField
                        label={t('wz:fields.quantity')}
                        wide={isEditMode}
                        emphasis
                      >
                        {isEditMode ? (
                          <WarehouseQuantityField
                            line={item}
                            onChange={(patch) => updateItemQuantity(index, patch)}
                            pricingQuantity={item.saleQuantity}
                            pricingUnit={item.saleUnit}
                            maxQuantity={WZ_QUANTITY_MAX}
                            unitEditable
                            unitLabel={t('wz:fields.unit')}
                            maxConvertedQuantity={WAREHOUSE_CONVERTED_QUANTITY_MAX}
                            noDimensionsLabel={t('wz:conversion.noDimensions')}
                          />
                        ) : formatWarehouseQuantity(item.quantity, item.unit, i18n.language)}
                      </WZItemField>
                      {!isEditMode && (
                        <WZItemField label={t('wz:fields.conversion')}>
                          {item.conversionUnit ? (conversionLabel || '—') : '—'}
                        </WZItemField>
                      )}
                    </WZItemCard>
                  );
                })}
              </WZItemsList>
            </Card>
          </Col>

          <Col span={24}>
            <Card style={{ marginBottom: 24 }} styles={{ body: { padding: 0 } }}>
              <Collapse
                ghost
                defaultActiveKey={[]}
                items={[
                  {
                    key: 'transport',
                    label: (
                      <Space size={8}>
                        <span style={{ fontWeight: 600 }}>{t('wz:sections.transport')}</span>
                        <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
                          ({t('wz:transport.optionalTag')})
                        </Typography.Text>
                      </Space>
                    ),
                    children: (
                      <>
                        <Typography.Paragraph type="secondary">
                          {t('wz:transport.autofillHint')}
                        </Typography.Paragraph>
                        <WZTransportSummary transports={transports} />
                        <Row gutter={16} style={{ marginTop: 20 }}>
                          <Col xs={24} md={8}>
                            <Form.Item
                              label={t('wz:fields.vehicleRegistration')}
                              name="vehicleRegistration"
                            >
                              <Input disabled={!isEditMode} placeholder={t('wz:placeholders.vehicleRegistration')} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    ),
                  },
                ]}
              />
            </Card>
          </Col>

          <Col span={24}>
            <Card title={t('wz:sections.signatureAndNotes')} style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.issuedBy')}
                    name="issuedBy"
                    rules={[
                      { required: true, message: t('wz:validation.issuedByRequired') },
                      { max: 100, message: t('wz:validation.personNameMax') },
                    ]}
                  >
                    <Input disabled={!isEditMode} placeholder={t('wz:placeholders.issuedBy')} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label={t('wz:fields.receivedBy')}
                    name="receivedBy"
                    rules={[{ max: 100, message: t('wz:validation.personNameMax') }]}
                  >
                    <Input disabled={!isEditMode} placeholder={t('wz:placeholders.receivedBy')} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item
                    label={t('wz:fields.notes')}
                    name="notes"
                  >
                    <TextArea rows={4} disabled={!isEditMode} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {isEditMode && (
            <Col span={24}>
              <Space>
                <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
                  {t('common:save')}
                </Button>
                <Button onClick={() => navigate('/app/wz')}>
                  {t('common:cancel')}
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </Form>

      {!isNewDocument && !isEditMode && wzDocument && canEdit && (
        <Card title={t('wz:sections.statusActions')} style={{ marginTop: 24 }}>
          <Space>
            {statusTransitionOptions.includes(WZStatus.CONFIRMED) && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => handleStatusChange(WZStatus.CONFIRMED)}
              >
                {t('wz:actions.confirm')}
              </Button>
            )}
            {statusTransitionOptions.includes(WZStatus.RELEASED) && (
              <Space size={6}>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => handleStatusChange(WZStatus.RELEASED)}
                >
                  {t('wz:actions.markAsReleased')}
                </Button>
                <Tooltip title={t('wz:operations.releaseInventoryEffect')}>
                  <InfoCircleOutlined
                    tabIndex={0}
                    aria-label={t('wz:operations.releaseInventoryEffect')}
                    style={{ color: '#1677ff', cursor: 'help' }}
                  />
                </Tooltip>
              </Space>
            )}
            {statusTransitionOptions.includes(WZStatus.RECEIVED) && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={() => handleStatusChange(WZStatus.RECEIVED)}
              >
                {t('wz:actions.markAsReceived')}
              </Button>
            )}
            {statusTransitionOptions.includes(WZStatus.CANCELLED) && (
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => handleStatusChange(WZStatus.CANCELLED)}
              >
                {t('wz:actions.cancel')}
              </Button>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default WZDetail;
